import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Plus, Loader2, Search, X } from 'lucide-react';
import { apiService, PanelGradeDTO, PanelUserDTO, type ProjectGroupDTO } from '@/services/api.service';
import { useAuthStore } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import { useActiveGameGroup, useAllowedGameGroups, useGameGroupsStore } from '@/stores/gameGroups.store';
import { useNotificationsStore } from '@/stores/notifications.store';
import { GAMES, supportsGameGroups } from '@/config/games';
import { assignableGroups, assignmentAllowsGroup } from '@/utils/gameGroups';
import styles from './AdminPage.module.scss';

/** Espace Administration : attribution des grades aux utilisateurs. La CRÉATION des grades vit
 * désormais dans les Paramètres généraux (écrou du header), avec le reste des réglages globaux. */
export function AdminPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = !!user?.isAdmin;
  const [grades, setGrades] = useState<PanelGradeDTO[]>([]);
  const [users, setUsers] = useState<PanelUserDTO[]>([]);
  const [loaded, setLoaded] = useState(false);
  const loadSeq = useRef(0);

  // La liste des grades sert à l'attribution (chips) ; leur édition est dans les Paramètres.
  // Le chargement n'occupe la page qu'au premier passage : une relecture après une modification
  // garde la liste montée (recherche, sections dépliées), et seule la plus récente s'applique.
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const [g, u] = await Promise.all([
      apiService.getPanelGrades(),
      apiService.getPanelUsers(),
    ]);
    if (seq !== loadSeq.current) return;
    setGrades(g.data?.grades ?? []);
    setUsers(u.data?.users ?? []);
    setLoaded(true);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Administration</h1>
        <p className={styles.subtitle}>Attribue les grades aux utilisateurs du projet sélectionné et, pour un projet à groupes de jeu, les groupes qu'ils supervisent (la liste suit alors le groupe choisi dans le bandeau). La création des grades et les paramètres généraux (admins, fonctionnalités, projets, grades protégés) sont derrière l'écrou du bandeau.</p>
      </header>

      {!loaded ? (
        <div className={styles.center}><Loader2 size={22} className={styles.spin} /> Chargement…</div>
      ) : (
        <UsersTab
          users={users}
          grades={grades}
          isAdmin={isAdmin}
          canAssign={!!user?.canAssignGrades}
          assignable={user?.assignableGradeIds ?? []}
          onReload={load}
        />
      )}
    </div>
  );
}

/**
 * Après une modification de ses groupes, le membre ne relève-t-il plus que d'autres groupes que
 * ceux du caller (affectation qui nomme des groupes, aucun à lui) ? L'API le retire alors de la
 * liste d'un caller limité à certains groupes. `groups` null = tous les groupes : il reste.
 */
function leftCallerGroups(groups: string[] | null | undefined, callerGroups: string[]): boolean {
  return Array.isArray(groups) && groups.length > 0 && !groups.some((k) => callerGroups.includes(k));
}

function timeAgo(ts: number): string {
  if (!ts) return '—';
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return "à l'instant";
  const m = Math.floor(s / 60); if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
}

function UsersTab({ users, grades, isAdmin, canAssign, assignable, onReload }: {
  users: PanelUserDTO[]; grades: PanelGradeDTO[];
  isAdmin: boolean; canAssign: boolean; assignable: string[];
  onReload: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState<string | null>(null); // sub du user dont le menu « + » est ouvert
  const [showNoAccess, setShowNoAccess] = useState(false);
  const [showAllGroups, setShowAllGroups] = useState(false);
  // Le filtre projet n'est plus un onglet interne : c'est le PROJET SÉLECTIONNÉ dans le bandeau
  // (GameSwitcher) qui fait foi, comme partout ailleurs dans le panel.
  const activeGameId = useGameStore((s) => s.activeGameId);
  const activeGameName = GAMES.find((g) => g.id === activeGameId)?.name ?? activeGameId;

  // Groupes de jeu du projet sélectionné (NationsGlory : Nova, Iris...) : catalogue lu par le panel,
  // libellés et couleurs compris. Vide pour un projet sans groupes : aucun contrôle affiché.
  const groupsEntry = useGameGroupsStore((s) => s.entries[activeGameId]);
  const projectGroups = useMemo(
    () => (supportsGameGroups(activeGameId) ? assignableGroups(groupsEntry) : []),
    [activeGameId, groupsEntry]);
  // Groupe choisi dans le bandeau : la liste montre ceux qui y ont accès. Null pour un projet sans
  // groupes (Paladium) ou sans groupe disponible : liste du projet entier, comme avant les groupes.
  const activeGroup = useActiveGameGroup();
  const viewGroup = activeGroup && projectGroups.some((g) => g.key === activeGroup.key) ? activeGroup : null;
  const scopeName = viewGroup ? `${activeGameName} ${viewGroup.label}` : activeGameName;
  // Groupes que le caller peut accorder : les siens (null = tous).
  const callerGroups = useAllowedGameGroups(activeGameId);
  const notifyError = useNotificationsStore((s) => s.error);
  const notifyInfo = useNotificationsStore((s) => s.info);

  const saveGameGroups = async (u: PanelUserDTO, next: string[] | null) => {
    setBusy(u.sub);
    const res = await apiService.setPanelUserGameGroups(u.sub, activeGameId, next);
    if (res.error) {
      notifyError(`Groupes de ${u.handle} : ${res.error}`);
    } else if (!isAdmin && callerGroups && leftCallerGroups(res.data?.groups, callerGroups)) {
      // Même règle que l'API : membre d'autres groupes seulement, il quitte la liste du caller.
      notifyInfo(`${u.handle} n'est plus dans tes groupes de ${activeGameName} : il n'apparaît plus dans ta liste.`);
    }
    await onReload();
    setBusy(null);
  };

  // Le caller peut-il attribuer/retirer ce grade ? admin partout, sinon limité à sa liste blanche.
  const canToggle = (gradeId: string) => isAdmin || (canAssign && assignable.includes(gradeId));

  const toggleGrade = async (u: PanelUserDTO, gradeId: string) => {
    if (!canToggle(gradeId)) return;
    const current = u.gradeIds ?? [];
    const next = current.includes(gradeId) ? current.filter((g) => g !== gradeId) : [...current, gradeId];
    setBusy(u.sub);
    await apiService.setPanelUserGrades(u.sub, next);
    await onReload();
    setBusy(null);
  };

  const gradeById = useMemo(() => new Map(grades.map((g) => [g.id, g] as const)), [grades]);

  // Projets où le user a AU MOINS une permission (admin = tous, admin de projet = ce projet).
  // Sert au filtrage par onglet projet.
  const projectsOf = (u: PanelUserDTO): Set<string> => {
    if (u.admin) return new Set(GAMES.map((g) => g.id));
    const s = new Set<string>();
    (u.adminProjects ?? []).forEach((p) => s.add(p));
    (u.gradeIds ?? []).forEach((id) => {
      const g = gradeById.get(id);
      (g?.permissions ?? []).forEach((p) => { const proj = p.split('.')[0]; if (proj) s.add(proj); });
    });
    return s;
  };

  // Grade PRÉDOMINANT = le plus haut dans la hiérarchie (grades est déjà trié admin d'abord, puis rang).
  const primaryGradeId = (u: PanelUserDTO): string | null => {
    const owned = new Set(u.gradeIds ?? []);
    return grades.find((g) => owned.has(g.id))?.id ?? null;
  };

  // Recherche (pseudo/email), puis séparation accès / sans accès au projet sélectionné. Les
  // « sans accès » ne disparaissent pas : ils restent derrière un dépliant, sans quoi il serait
  // impossible d'attribuer un premier grade à un nouveau venu.
  const searching = query.trim().length > 0;
  const matched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.handle ?? '').toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q));
  }, [users, query]);

  // Vue d'un groupe de jeu : liste = accès au projet ET au groupe (affectation qui le contient, ou
  // aucune affectation = tous les groupes). Les admins (global ou du projet), qui ont tous les
  // groupes, vont dans une section à part, repliée. Sans groupe : liste du projet, comme avant.
  const { visible, allGroups, noAccess } = useMemo(() => {
    const out = { visible: [] as PanelUserDTO[], allGroups: [] as PanelUserDTO[], noAccess: [] as PanelUserDTO[] };
    matched.forEach((u) => {
      if (viewGroup && (u.admin || (u.adminProjects ?? []).includes(activeGameId))) out.allGroups.push(u);
      else if (!projectsOf(u).has(activeGameId)) out.noAccess.push(u);
      else if (viewGroup && !assignmentAllowsGroup(u.gameGroups, activeGameId, viewGroup.key)) out.noAccess.push(u);
      else out.visible.push(u);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matched, activeGameId, grades, viewGroup?.key]);
  // Une recherche montre aussi les admins qui y répondent, sans avoir à déplier leur section.
  const allGroupsOpen = showAllGroups || searching;

  // Regroupement par grade prédominant (ordre hiérarchique), puis « Sans grade » à la fin.
  const sections = useMemo(() => {
    const byGrade = new Map<string, PanelUserDTO[]>();
    const noGrade: PanelUserDTO[] = [];
    visible.forEach((u) => {
      const pid = primaryGradeId(u);
      if (!pid) { noGrade.push(u); return; }
      if (!byGrade.has(pid)) byGrade.set(pid, []);
      byGrade.get(pid)!.push(u);
    });
    const out: { key: string; label: string; color?: string; users: PanelUserDTO[] }[] = [];
    grades.forEach((g) => {
      const us = byGrade.get(g.id);
      if (us && us.length) out.push({ key: g.id, label: `${g.name}${g.admin ? ' (admin)' : ''}`, color: g.color, users: us });
    });
    if (noGrade.length) out.push({ key: '__none__', label: 'Sans grade', users: noGrade });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, grades]);

  const renderUser = (u: PanelUserDTO) => {
    const assigned = u.gradeIds ?? [];
    // Grades possédés (dans l'ordre hiérarchique) et grades ajoutables (non possédés + attribuables par le caller).
    const owned = grades.filter((g) => assigned.includes(g.id));
    const addable = grades.filter((g) => !assigned.includes(g.id) && canToggle(g.id));
    return (
      <div key={u.sub} className={styles.userRow}>
        <div className={styles.userAvatar}>
          {u.avatar ? <img src={u.avatar} alt={u.handle} /> : <span>{u.handle?.charAt(0).toUpperCase() ?? '?'}</span>}
        </div>
        <div className={styles.userInfo}>
          <div className={styles.userName}>{u.handle}</div>
          <div className={styles.userMeta}>{u.email ?? '—'} · connexion {timeAgo(u.lastLogin)}</div>
          {projectGroups.length > 0 && (
            <GameGroupsControl
              user={u}
              projectId={activeGameId}
              projectName={activeGameName}
              groups={projectGroups}
              canEdit={isAdmin || canAssign}
              callerGroups={isAdmin ? null : callerGroups}
              busy={busy === u.sub}
              onSave={(next) => saveGameGroups(u, next)}
            />
          )}
        </div>
        <div className={styles.chipRow}>
          {owned.length === 0 && <span className={styles.noGrade}>aucun grade — pas d'accès</span>}
          {owned.map((g) => {
            const removable = canToggle(g.id);
            return (
              <span key={g.id} className={styles.assignedChip} title={g.admin ? 'Grade admin' : g.name}>
                <span className={styles.chipDot} style={{ background: g.color }} />
                {g.name}{g.admin ? ' (admin)' : ''}
                {removable && (
                  <button
                    type="button"
                    className={styles.chipRemove}
                    disabled={busy === u.sub}
                    onClick={() => toggleGrade(u, g.id)}
                    title="Retirer ce grade"
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            );
          })}
          {addable.length > 0 && (
            <span className={styles.addWrap}>
              <button
                type="button"
                className={styles.addBtn}
                disabled={busy === u.sub}
                onClick={() => setAddOpen(addOpen === u.sub ? null : u.sub)}
                title="Ajouter un grade"
              >
                <Plus size={14} />
              </button>
              {addOpen === u.sub && (
                <>
                  <div className={styles.addOverlay} onClick={() => setAddOpen(null)} />
                  <ul className={styles.addMenu}>
                    {addable.map((g) => (
                      <li key={g.id}>
                        <button
                          type="button"
                          className={styles.addMenuItem}
                          onClick={() => { setAddOpen(null); toggleGrade(u, g.id); }}
                        >
                          <span className={styles.chipDot} style={{ background: g.color }} />
                          {g.name}{g.admin ? ' (admin)' : ''}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (users.length === 0) return <p className={styles.muted}>Aucun utilisateur connecté pour l'instant.</p>;

  return (
    <div>
      {viewGroup ? (
        <p className={styles.muted} style={{ marginBottom: 10 }}>
          Utilisateurs ayant accès à <strong>{scopeName}</strong> (change de groupe ou de projet dans
          le bandeau pour voir les autres), regroupés par grade. Les admins, qui ont tous les groupes,
          sont à part.
        </p>
      ) : (
        <p className={styles.muted} style={{ marginBottom: 10 }}>
          Utilisateurs ayant accès à <strong>{activeGameName}</strong> (change de projet dans le
          bandeau pour voir les autres), regroupés par grade.
        </p>
      )}

      <div style={{ position: 'relative' }}>
        <Search size={15} style={{ position: 'absolute', left: 10, top: 10, opacity: 0.5 }} />
        <input
          className={styles.searchInput}
          style={{ paddingLeft: 32 }}
          placeholder="Rechercher un utilisateur (pseudo, email)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {sections.length === 0 ? (
        <p className={styles.muted}>
          {viewGroup
            ? (query
              ? `Aucun membre de ${scopeName}${allGroups.length ? ' (hors admins)' : ''} ne correspond à « ${query} ».`
              : `Aucun utilisateur n'a accès à ${scopeName}${allGroups.length ? ' en dehors des admins' : ''}.`)
            : (query ? `Aucun utilisateur ne correspond à « ${query} ».` : `Aucun utilisateur n'a accès à ${activeGameName}.`)}
        </p>
      ) : (
        sections.map((sec) => (
          <div key={sec.key}>
            <div className={styles.groupTitle}>
              {sec.color && <span className={styles.chipDot} style={{ background: sec.color, marginRight: 6 }} />}
              {sec.label} ({sec.users.length})
            </div>
            <div className={styles.list}>{sec.users.map(renderUser)}</div>
          </div>
        ))
      )}

      {viewGroup && allGroups.length > 0 && (
        <div className={styles.allGroups}>
          <button
            type="button"
            className={styles.allGroupsToggle}
            aria-expanded={allGroupsOpen}
            disabled={searching}
            onClick={() => setShowAllGroups((v) => !v)}
            title={searching
              ? 'Ouvert pendant la recherche'
              : `${allGroupsOpen ? 'Replier' : 'Déplier'} : admins globaux et admins de ${activeGameName}`}
          >
            <ChevronRight size={14} className={`${styles.chevron} ${allGroupsOpen ? styles.chevronOpen : ''}`} />
            <span className={styles.allGroupsTitle}>Accès à tous les groupes ({allGroups.length})</span>
            {!allGroupsOpen && (
              <span className={styles.allGroupsNames}>{allGroups.map((u) => u.handle).join(', ')}</span>
            )}
          </button>
          {allGroupsOpen && (
            <>
              <p className={`${styles.muted} ${styles.allGroupsHint}`}>
                Admins globaux et admins de {activeGameName} : ils ont toujours tous les groupes, y
                compris ceux ajoutés plus tard.
              </p>
              <div className={styles.list}>{allGroups.map(renderUser)}</div>
            </>
          )}
        </div>
      )}

      {noAccess.length > 0 && (
        <div>
          <button
            type="button"
            className={styles.iconBtn}
            style={{ marginTop: 16 }}
            onClick={() => setShowNoAccess((v) => !v)}
          >
            {showNoAccess ? 'Masquer' : 'Afficher'} les utilisateurs sans accès à {scopeName} ({noAccess.length})
          </button>
          {showNoAccess && <div className={styles.list} style={{ marginTop: 8 }}>{noAccess.map(renderUser)}</div>}
        </div>
      )}
    </div>
  );
}

/**
 * Groupes de jeu supervisés par un utilisateur sur le projet sélectionné (NationsGlory : Nova,
 * Iris...). « Tous les groupes » = aucune affectation : tous les groupes du projet, y compris ceux
 * ajoutés plus tard (comportement d'avant l'affectation). Sinon, seuls les groupes cochés.
 *
 * Un admin (global ou du projet) a toujours tous les groupes. Un gestionnaire non admin ne coche ou
 * ne décoche que les groupes qu'il a lui-même ; l'API applique la même règle.
 */
function GameGroupsControl({ user, projectId, projectName, groups, canEdit, callerGroups, busy, onSave }: {
  user: PanelUserDTO;
  projectId: string;
  projectName: string;
  groups: ProjectGroupDTO[];
  canEdit: boolean;
  /** Groupes que le caller peut accorder, null = tous. */
  callerGroups: string[] | null;
  busy: boolean;
  onSave: (next: string[] | null) => void;
}) {
  const keys = groups.map((g) => g.key);
  const isTargetAdmin = user.admin || (user.adminProjects ?? []).includes(projectId);
  const stored = user.gameGroups?.[projectId];
  const all = !Array.isArray(stored);
  // Groupes effectivement supervisés, limités au catalogue actuel (un groupe retiré du CICD disparaît).
  const selected = all ? keys : keys.filter((k) => stored.includes(k));

  if (isTargetAdmin) {
    return (
      <div className={styles.groupsRow}>
        <span className={styles.groupsCaption}>Groupes</span>
        <span className={styles.groupsNote}>tous les groupes (admin)</span>
      </div>
    );
  }

  const canGrant = (key: string) => canEdit && (callerGroups === null || callerGroups.includes(key));
  // « Tous les groupes » inclut les groupes futurs : seul un caller qui les a tous peut le changer.
  const canToggleAll = canEdit && callerGroups === null;
  const readOnly = "Lecture seule : il faut le droit d'attribuer des grades";

  const toggleGroup = (key: string) => {
    if (!canGrant(key)) return;
    const next = selected.includes(key) ? selected.filter((k) => k !== key) : keys.filter((k) => k === key || selected.includes(k));
    onSave(next);
  };

  return (
    <div className={styles.groupsRow}>
      <span className={styles.groupsCaption}>Groupes</span>
      <label
        className={styles.groupsAll}
        title={!canEdit ? readOnly : canToggleAll
          ? `Tous les groupes de ${projectName}, y compris ceux ajoutés plus tard`
          : 'Seul un responsable qui a tous les groupes peut changer ce réglage'}
      >
        <input
          type="checkbox"
          checked={all}
          disabled={!canToggleAll || busy}
          onChange={() => onSave(all ? keys : null)}
        />
        Tous les groupes
      </label>
      {groups.map((g) => {
        const on = selected.includes(g.key);
        const editable = canGrant(g.key);
        return (
          <button
            key={g.key}
            type="button"
            className={`${styles.chip} ${on ? styles.chipActive : ''}`}
            aria-pressed={on}
            disabled={!editable || busy}
            onClick={() => toggleGroup(g.key)}
            title={!canEdit ? readOnly : editable
              ? `${on ? 'Retirer' : 'Accorder'} la supervision de ${g.label}`
              : `Tu ne peux pas accorder ${g.label} : tu n'y es pas affecté`}
          >
            <span className={styles.chipDot} style={{ background: g.color ?? 'var(--color-text-secondary)' }} />
            {g.label}
          </button>
        );
      })}
      {selected.length === 0 && <span className={styles.noGrade}>aucun groupe : rien à superviser sur {projectName}</span>}
    </div>
  );
}
